import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { TextField } from '@refinedev/antd';
import { Button, Flex, Form, Input, message, Modal, Space, Tooltip } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CompanyLinkInfo from '@/components/general/CompanyLinkInfo';
import EditableModal, { useEditableModal } from '@/components/general/EditableModal';
import {
  EditableInfoItem,
  ShowInfoGrid,
  ShowInfoItem,
} from '@/components/general/ShowInfo/ShowInfo';
import UploadedFileList from '@/components/general/UploadFile/UploadedFileList';
import UploadFileComponent from '@/components/general/UploadFile/UploadFile';
import { useCountryContext } from '@/contexts/CountryContext';
import { useUserContext } from '@/contexts/UserContext';
import FileData from '@/model/General/FileData';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { downloadFile } from '@/utils/downloadFile';

import { getTaxFileCode, getTaxFilePhotos } from '../shipmentTaxFileUtils';

/** Metadata: extrae el nombre real del archivo desde una URL, descartando query params */
const getFileNameFromUrl = (url: string): string =>
  decodeURIComponent(url.split('?')[0].split('/').pop() ?? 'archivo-tributario');

interface ShipmentShowPaymentProps {
  record?: Shipment;
  packageItems?: PackageItem[];
  refetch?: () => Promise<any>;
}

// Metadata: normaliza la obtención de facturas únicas para mostrar y editar el mismo dato.
const getUniqueBillingNumbers = (packageItems?: PackageItem[]) => {
  if (!packageItems) return [];

  const billingNumbers: string[] = [];

  packageItems.forEach(item => {
    if (item.billing_number && !billingNumbers.includes(item.billing_number)) {
      billingNumbers.push(item.billing_number);
    }
  });

  return billingNumbers;
};

const ShipmentShowPayment = (props: ShipmentShowPaymentProps) => {
  const { isBrasilSelected } = useCountryContext();
  const { t } = useTranslation();
  const { record, packageItems, refetch } = props;
  const { userWorkArea } = useUserContext();
  const { packageItemRepository, packageRepository, shipmentRepository } = useRepositoriesContext();
  const { activeModal, openModal, closeModal } = useEditableModal<
    'sales_order' | 'billing_number'
  >();
  const billingNumbers = useMemo(() => getUniqueBillingNumbers(packageItems), [packageItems]);
  // --- Estado para archivos tributarios ---
  const [taxUploadFiles, setTaxUploadFiles] = useState<FileData[]>([]);
  const [taxUploadModalOpen, setTaxUploadModalOpen] = useState(false);
  /**
   * Se hidratan los archivos tributarios existentes desde record.photos
   * filtrando por el código de descripción tax_file_{id}.
   */
  const existingTaxFiles = useMemo(() => {
    if (!record?.id || !record?.photos) return [];
    const taxPhotos = getTaxFilePhotos(record.photos, record.id);

    return taxPhotos.map(
      f =>
        new FileData({
          id: f.id,
          title: getFileNameFromUrl(f.photo),
          file: f.photo,
        }),
    );
  }, [record?.id, record?.photos]);
  /**
   * Determina si se permite cargar archivos tributarios.
   * Solo administración puede subir, y solo en estado ADMIN_MANAGEMENT (gestión administrativa).
   */
  const canUploadTaxFile =
    (userWorkArea === USER_WORK_AREA.ADMINISTRATION || userWorkArea === USER_WORK_AREA.LOGISTICS) &&
    record?.status === PACKAGE_STATES.ADMIN_MANAGEMENT;
  /**
   * Metadata: handler de eliminación de archivo tributario.
   * Marca el archivo como removido y lo elimina vía packageRepository.saveFiles.
   */
  const handleDeleteTaxFile = useCallback(
    (file: FileData) => {
      Modal.confirm({
        title: t('¿Estás seguro de que deseas eliminar este archivo tributario?'),
        content: t('Esta acción no se puede deshacer'),
        okText: t('Sí, eliminar'),
        okType: 'danger',
        cancelText: t('No'),
        onOk: () => {
          if (!record?.id) return;
          const fileToRemove = new FileData({ id: file.id, title: file.title, file: file.fileUrl });

          fileToRemove.setRemoved(true);
          void packageRepository.saveFiles([fileToRemove], record.id).then(() => {
            void refetch?.();
          });
        },
      });
    },
    [t, record?.id, packageRepository, refetch],
  );

  return (
    <ShowInfoGrid>
      <ShowInfoItem label={t('Razón social')}>
        <CompanyLinkInfo company={record?.shipment_payment_company} />
      </ShowInfoItem>
      <ShowInfoItem label={isBrasilSelected ? 'CPF/CNPJ' : 'CUIT'}>
        <TextField value={record?.shipment_payment_cuit} />
      </ShowInfoItem>
      {record?.has_sale_items && (
        <EditableInfoItem
          label={t('Orden de venta')}
          action={
            userWorkArea === USER_WORK_AREA.LOGISTICS &&
            record?.status === PACKAGE_STATES.PENDIENTE && (
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => openModal('sales_order')}
              />
            )
          }>
          <TextField value={record?.external_document || '-'} />
        </EditableInfoItem>
      )}
      {(record?.has_sale_items || billingNumbers.length > 0) && (
        <EditableInfoItem
          label={billingNumbers.length > 1 ? t('Números de factura') : t('Número de factura')}
          action={
            userWorkArea === USER_WORK_AREA.ADMINISTRATION &&
            record?.status === PACKAGE_STATES.GESTION_PAGO && (
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => openModal('billing_number')}
              />
            )
          }>
          {billingNumbers.length === 0 ? (
            '-'
          ) : (
            <ul className="ShipmentShowValueList">
              {billingNumbers.map(num => (
                <li key={num}>{num}</li>
              ))}
            </ul>
          )}
        </EditableInfoItem>
      )}
      {/* --- Sección de archivo tributario ---
       * Permite cargar un archivo tributario (ej. factura fiscal)
       * que se guarda como foto del paquete con description = tax_file_{id}.
       * La carga se habilita solo para administración en estado ADMIN_MANAGEMENT. */}
      {isBrasilSelected && (
        <ShowInfoItem label={t('Archivo tributario')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {existingTaxFiles.length > 0 ? (
              existingTaxFiles.map(file => (
                <Space key={file.id} size="small" align="center">
                  <FileOutlined />
                  <a
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: 'break-all' }}>
                    {file.title}
                  </a>
                  <Tooltip title={t('Descargar')}>
                    <Button
                      icon={<DownloadOutlined />}
                      size="small"
                      type="text"
                      onClick={() => void downloadFile(file)}
                    />
                  </Tooltip>
                  {canUploadTaxFile && (
                    <Tooltip title={t('Eliminar')}>
                      <Button
                        icon={<DeleteOutlined />}
                        size="small"
                        type="text"
                        danger
                        onClick={() => handleDeleteTaxFile(file)}
                      />
                    </Tooltip>
                  )}
                </Space>
              ))
            ) : (
              <Flex gap={8} wrap>
                <TextField value={t('No hay archivo tributario')} />
                {canUploadTaxFile && (
                  <Button
                    icon={<UploadOutlined />}
                    size="small"
                    style={{ width: 'fit-content' }}
                    disabled={existingTaxFiles.length > 0}
                    onClick={() => setTaxUploadModalOpen(true)}>
                    {t('Cargar archivo tributario')}
                  </Button>
                )}
              </Flex>
            )}
          </div>
          {canUploadTaxFile && (
            <Modal
              title={t('Cargar archivo tributario')}
              open={taxUploadModalOpen}
              onCancel={() => {
                setTaxUploadModalOpen(false);
                setTaxUploadFiles([]);
              }}
              okText={t('Cargar')}
              cancelText={t('Cancelar')}
              okButtonProps={{ disabled: taxUploadFiles.length === 0 }}
              onOk={() => {
                Modal.confirm({
                  title: t('¿Estás seguro de que deseas cargar este archivo tributario?'),
                  content: t('Esta acción no se puede deshacer'),
                  okText: t('Sí'),
                  okType: 'primary',
                  cancelText: t('No'),
                  onOk: () => {
                    if (!record?.id) {
                      setTaxUploadModalOpen(false);
                      setTaxUploadFiles([]);

                      return;
                    }

                    const taxCode = getTaxFileCode(record.id);
                    // Limitar a un solo archivo tributario
                    const filesToUpload = taxUploadFiles.slice(0, 1);

                    void packageRepository.saveFiles(filesToUpload, record.id, taxCode).then(() => {
                      setTaxUploadFiles([]);
                      setTaxUploadModalOpen(false);
                      void refetch?.();
                    });
                  },
                });
              }}>
              <UploadFileComponent
                fileList={taxUploadFiles}
                setFileList={(updater: any) => {
                  // Limitar a máximo 1 archivo en la lista
                  setTaxUploadFiles(prev => {
                    const result = typeof updater === 'function' ? updater(prev) : updater;

                    if (result.length > 1) {
                      message.warning(t('Solo se permite un archivo tributario'));

                      return result.slice(-1);
                    }

                    return result;
                  });
                }}
              />
              {taxUploadFiles.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <UploadedFileList fileList={taxUploadFiles} setFileList={setTaxUploadFiles} />
                </div>
              )}
            </Modal>
          )}
        </ShowInfoItem>
      )}

      <EditableModal
        activeModal={activeModal}
        closeModal={closeModal}
        modalConfigByKey={{
          sales_order: {
            title: t('Editar orden de venta'),
            form: {
              initialValues: { externalDocument: record?.external_document },
              onFinish: async values => {
                if (
                  !record ||
                  values.externalDocument === undefined ||
                  values.externalDocument === record.external_document
                ) {
                  return false;
                }

                await shipmentRepository.updateShipmentExternalDocument(
                  record.id,
                  values.externalDocument,
                );
                await refetch?.();
              },
              children: (
                <Form.Item name="externalDocument">
                  <Input />
                </Form.Item>
              ),
            },
          },
          billing_number: {
            title: t('Editar número de factura'),
            form: {
              initialValues: { billingNumber: getUniqueBillingNumbers(packageItems).join(', ') },
              onFinish: async values => {
                if (!record || values.billingNumber === undefined) return false;

                const packageToUpdate =
                  packageItems?.filter(
                    item => item.item_shipped_reference === SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO,
                  ) || [];

                packageToUpdate.forEach(packageItem => {
                  packageItem.billing_number = values.billingNumber;
                  packageItem.toUpdate = true;
                });

                await packageItemRepository.savePackageItems(packageToUpdate, record.id);
                await refetch?.();
              },
              children: (
                <Form.Item name="billingNumber">
                  <Input />
                </Form.Item>
              ),
            },
          },
        }}
      />
    </ShowInfoGrid>
  );
};

export default ShipmentShowPayment;
